// src/services/apiConfig.js
// Shared API configuration utilities

import config from '../utils/config';

// Helper function to get cookie value
const getCookie = (name) => {
  const cookieName = name + "=";
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return "";
};

export const getApiBaseUrl = () => {
  const apiUrl = config.apiUrl();
  if (!apiUrl) {
    console.warn("REACT_APP_API_URL not set, using deployed API");
    console.log(
      "🔄 Using direct API Gateway endpoint for CI Tech Products Dev account"
    );
    return "https://ofrum3owxa.execute-api.us-east-1.amazonaws.com/development";
  }
  return apiUrl.replace(/\/$/, "");
};

export const getWebSocketUrl = (userId = null) => {
  const wsUrl = config.websocketUrl();
  
  let baseUrl;
  if (!wsUrl) {
    console.warn("REACT_APP_WEBSOCKET_URL not set, using deployed WebSocket API");
    console.log("🔄 Using production WebSocket API Gateway endpoint");
    baseUrl = "wss://mt7umnxifc.execute-api.us-east-1.amazonaws.com/prod";
  } else {
    baseUrl = wsUrl;
  }
  
  // Get JWT access token for authentication
  const accessToken = getCookie('accessToken');
  if (!accessToken) {
    console.error('No access token found for WebSocket authentication');
  }
  
  let url = `${baseUrl}?token=${encodeURIComponent(accessToken || '')}`;
  if (userId) {
    url += `&userId=${encodeURIComponent(userId)}`;
  }
  return url;
};

// Request Queue Configuration
export const QUEUE_CONFIG = {
  MAX_CONCURRENT_CHAT_REQUESTS: 3, // Max concurrent chat requests per client
  MAX_CONCURRENT_API_REQUESTS: 5, // Max concurrent API requests per client
  MAX_QUEUE_SIZE: 15, // Increased queue size to handle startup requests
  RETRY_DELAY: 1000, // Base retry delay in ms
  MAX_RETRIES: 3, // Max retry attempts
  THROTTLE_DELAY: 500, // Delay between requests in ms
};

// Request Queue Manager
class RequestQueueManager {
  constructor() {
    this.chatQueue = [];
    this.apiQueue = [];
    this.activeChatRequests = new Set();
    this.activeApiRequests = new Set();
    this.requestId = 0;
  }

  generateRequestId() {
    return `req_${++this.requestId}_${Date.now()}`;
  }

  async queueRequest(requestFn, requestType = "api", priority = "normal") {
    const requestId = this.generateRequestId();
    const isChat = requestType === "chat";
    const queue = isChat ? this.chatQueue : this.apiQueue;
    const activeRequests = isChat
      ? this.activeChatRequests
      : this.activeApiRequests;
    const maxConcurrent = isChat
      ? QUEUE_CONFIG.MAX_CONCURRENT_CHAT_REQUESTS
      : QUEUE_CONFIG.MAX_CONCURRENT_API_REQUESTS;

    console.log(
      `🔄 Queuing ${requestType} request ${requestId}, active: ${activeRequests.size}/${maxConcurrent}`
    );

    // Check if we can execute immediately
    if (activeRequests.size < maxConcurrent) {
      return this.executeRequest(requestFn, requestId, requestType);
    }

    // Check queue size limit
    if (queue.length >= QUEUE_CONFIG.MAX_QUEUE_SIZE) {
      console.warn(`⚠️ Request queue full (${queue.length}/${QUEUE_CONFIG.MAX_QUEUE_SIZE}), rejecting ${requestType} request`);
      throw new Error(`Request queue full (${queue.length} requests). Please wait for current requests to complete.`);
    }

    // Add to queue
    return new Promise((resolve, reject) => {
      const queueItem = {
        id: requestId,
        requestFn,
        resolve,
        reject,
        priority,
        timestamp: Date.now(),
        retries: 0,
      };

      // Insert based on priority (high priority first)
      if (priority === "high") {
        queue.unshift(queueItem);
      } else {
        queue.push(queueItem);
      }

      console.log(
        `📋 Request ${requestId} queued (position: ${queue.length}, priority: ${priority})`
      );
    });
  }

  async executeRequest(requestFn, requestId, requestType) {
    const isChat = requestType === "chat";
    const activeRequests = isChat
      ? this.activeChatRequests
      : this.activeApiRequests;

    activeRequests.add(requestId);
    console.log(`🚀 Executing ${requestType} request ${requestId}`);

    try {
      const result = await requestFn();
      console.log(`✅ Request ${requestId} completed successfully`);
      return result;
    } catch (error) {
      console.log(`❌ Request ${requestId} failed:`, error.message);
      throw error;
    } finally {
      activeRequests.delete(requestId);
      console.log(
        `🏁 Request ${requestId} finished, active: ${activeRequests.size}`
      );

      // Process next item in queue
      setTimeout(
        () => this.processQueue(requestType),
        QUEUE_CONFIG.THROTTLE_DELAY
      );
    }
  }

  async processQueue(requestType) {
    const isChat = requestType === "chat";
    const queue = isChat ? this.chatQueue : this.apiQueue;
    const activeRequests = isChat
      ? this.activeChatRequests
      : this.activeApiRequests;
    const maxConcurrent = isChat
      ? QUEUE_CONFIG.MAX_CONCURRENT_CHAT_REQUESTS
      : QUEUE_CONFIG.MAX_CONCURRENT_API_REQUESTS;

    if (queue.length === 0 || activeRequests.size >= maxConcurrent) {
      return;
    }

    const queueItem = queue.shift();
    console.log(`📤 Processing queued ${requestType} request ${queueItem.id}`);

    try {
      const result = await this.executeRequest(
        queueItem.requestFn,
        queueItem.id,
        requestType
      );
      queueItem.resolve(result);
    } catch (error) {
      // Retry logic
      if (
        queueItem.retries < QUEUE_CONFIG.MAX_RETRIES &&
        this.shouldRetry(error)
      ) {
        queueItem.retries++;
        const retryDelay =
          QUEUE_CONFIG.RETRY_DELAY * Math.pow(2, queueItem.retries - 1); // Exponential backoff

        console.log(
          `🔄 Retrying request ${queueItem.id} (attempt ${queueItem.retries}/${QUEUE_CONFIG.MAX_RETRIES}) in ${retryDelay}ms`
        );

        setTimeout(() => {
          if (queueItem.priority === "high") {
            queue.unshift(queueItem);
          } else {
            queue.push(queueItem);
          }
          this.processQueue(requestType);
        }, retryDelay);
      } else {
        console.log(
          `💥 Request ${queueItem.id} failed permanently after ${queueItem.retries} retries`
        );
        queueItem.reject(error);
      }
    }
  }

  shouldRetry(error) {
    // Retry on network errors, timeouts, and 5xx server errors
    return (
      error.message.includes("timeout") ||
      error.message.includes("network") ||
      error.message.includes("500") ||
      error.message.includes("502") ||
      error.message.includes("503") ||
      error.message.includes("504")
    );
  }

  getQueueStatus() {
    return {
      chatQueue: {
        active: this.activeChatRequests.size,
        queued: this.chatQueue.length,
        maxConcurrent: QUEUE_CONFIG.MAX_CONCURRENT_CHAT_REQUESTS,
      },
      apiQueue: {
        active: this.activeApiRequests.size,
        queued: this.apiQueue.length,
        maxConcurrent: QUEUE_CONFIG.MAX_CONCURRENT_API_REQUESTS,
      },
    };
  }

  clearQueue() {
    this.chatQueue.forEach((item) => item.reject(new Error("Queue cleared")));
    this.apiQueue.forEach((item) => item.reject(new Error("Queue cleared")));
    this.chatQueue = [];
    this.apiQueue = [];
    console.log("🧹 Request queues cleared");
  }
}

// Global request queue manager instance
export const requestQueueManager = new RequestQueueManager();

// Dynamic headers that include authentication
export const getApiHeaders = () => {
  const idToken = getCookie("idToken");
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
    console.log("🔐 API request authenticated with ID token");
  } else {
    console.log(
      "ℹ️ API request without authentication (OK for direct API Gateway testing)"
    );
  }

  return headers;
};

// Legacy export for backward compatibility
export const API_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

// Timeout configurations for different endpoints
export const API_TIMEOUTS = {
  DEFAULT: 30000, // 30 seconds
  CHAT: 300000, // 5 minutes for chat requests (matches Lambda timeout)
  LONG_RUNNING: 300000, // 5 minutes for very complex operations (matches Lambda timeout)
  POLLING: 5000, // 5 seconds for polling checks
};

// Create a fetch wrapper with timeout support and queue management
export const fetchWithTimeout = async (
  url,
  options = {},
  timeoutMs = API_TIMEOUTS.DEFAULT,
  requestType = "api",
  priority = "normal"
) => {
  // Determine if this is a chat request based on URL
  const isChatRequest = url.includes("/chat");
  const actualRequestType = isChatRequest ? "chat" : requestType;

  // Create the actual fetch function
  const fetchFn = async () => {
    const controller = new AbortController();
    const startTime = Date.now();

    // Set up the timeout
    const timeoutId = setTimeout(() => {
      const elapsedTime = Date.now() - startTime;
      console.log(
        `⏰ Aborting request after ${elapsedTime}ms (timeout was ${timeoutMs}ms)`
      );
      controller.abort();
    }, timeoutMs);

    try {
      console.log(
        `🚀 Starting ${actualRequestType} fetch with ${timeoutMs}ms timeout`
      );
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        cache: "no-cache", // Force fresh requests to avoid CORS cache issues
        mode: "cors", // Explicitly set CORS mode
      });

      const elapsedTime = Date.now() - startTime;
      console.log(`✅ Fetch completed in ${elapsedTime}ms`);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      clearTimeout(timeoutId);

      console.log(
        `❌ Fetch failed after ${elapsedTime}ms:`,
        error.name,
        error.message
      );

      // Handle CORS errors specifically
      if (
        error.message.includes("Failed to fetch") &&
        window.location.hostname !== "localhost"
      ) {
        const corsError = new Error(
          `CORS Error: API server is not configured to accept requests from ${window.location.origin}. ` +
            `The API Gateway needs to be updated to include this domain in its CORS settings.`
        );
        corsError.name = "CORSError";
        corsError.originalError = error;
        throw corsError;
      }

      if (error.name === "AbortError") {
        throw new Error(
          `Request timed out after ${
            elapsedTime / 1000
          } seconds (configured timeout: ${timeoutMs / 1000}s)`
        );
      }
      throw error;
    }
  };

  // Add cache busting for CORS issues
  const separator = url.includes("?") ? "&" : "?";
  const cacheBustUrl = `${url}${separator}_t=${Date.now()}`;

  // Update fetchFn to use cache-busted URL
  const cacheBustFetchFn = async () => {
    const controller = new AbortController();
    const startTime = Date.now();

    // Set up the timeout
    const timeoutId = setTimeout(() => {
      const elapsedTime = Date.now() - startTime;
      console.log(
        `⏰ Aborting request after ${elapsedTime}ms (timeout was ${timeoutMs}ms)`
      );
      controller.abort();
    }, timeoutMs);

    try {
      console.log(
        `🚀 Starting ${actualRequestType} fetch with ${timeoutMs}ms timeout (cache-busted)`
      );
      const response = await fetch(cacheBustUrl, {
        ...options,
        signal: controller.signal,
        cache: "no-cache",
        mode: "cors",
      });

      const elapsedTime = Date.now() - startTime;
      console.log(`✅ Fetch completed in ${elapsedTime}ms`);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      clearTimeout(timeoutId);

      console.log(
        `❌ Fetch failed after ${elapsedTime}ms:`,
        error.name,
        error.message
      );

      if (
        error.message.includes("Failed to fetch") &&
        window.location.hostname !== "localhost"
      ) {
        const corsError = new Error(
          `CORS Error: API server is not configured to accept requests from ${window.location.origin}. ` +
            `The API Gateway needs to be updated to include this domain in its CORS settings.`
        );
        corsError.name = "CORSError";
        corsError.originalError = error;
        throw corsError;
      }

      if (error.name === "AbortError") {
        throw new Error(
          `Request timed out after ${
            elapsedTime / 1000
          } seconds (configured timeout: ${timeoutMs / 1000}s)`
        );
      }
      throw error;
    }
  };

  // Queue the request
  return requestQueueManager.queueRequest(
    cacheBustFetchFn,
    actualRequestType,
    priority
  );
};

// Common error handler
export const handleApiError = async (response) => {
  if (!response.ok) {
    let errorMessage = `HTTP error! Status: ${response.status}`;
    try {
      const errorData = await response.text();
      if (errorData) {
        errorMessage += `, Message: ${errorData}`;
      }
    } catch (e) {
      // If we can't read the error, just use the status
    }
    throw new Error(errorMessage);
  }
  return response;
};
